import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { AlertTriangle, ChevronRight, RefreshCw } from "lucide-react";
import { useUIContext } from "@/contexts/UIContext";
import { useSeesLegacySyncErrors } from "@/hooks/useSeesLegacySyncErrors";
import { syncBanner, type SalefishSyncState } from "@/lib/syncStaleness";
import { cn } from "@/lib/utils";
import { fetchJson } from "@/lib/queryClient";
import { useTranslation } from "@/hooks/useTranslation";

/**
 * "Showing data as of 2:15 p.m." — the notice a SaleFish-synced project wears
 * when its last pull failed or stopped happening (E7). See lib/syncStaleness.ts
 * for what is said to whom.
 *
 * Reads the same query (and so the same cache) as the SaleFish panel in
 * Project Settings, so on a project that is not linked it costs one small
 * request and renders nothing. It polls every five minutes: the cron pulls
 * every minute, so a failure is usually cleared by the next look. Polling does
 * not keep a session alive — only /api/auth/activity does (CLAUDE.md, Session
 * timeouts).
 *
 * Place it once, above the page content, inside a project route: AppShell's
 * <main> (the lead's file), or the top of a page.
 */
export function SyncStatusBanner({ projectId: explicit, className }: { projectId?: string | null; className?: string }) {
  const t = useTranslation();
  const { projectId: fromContext } = useUIContext();
  const projectId = explicit ?? fromContext;
  // The whole notice is a sync error, so it is a SaleFish Admin's alone
  // (shared/legacySyncErrors.ts): anyone else reads nothing, not even the poll.
  const isPlatformAdmin = useSeesLegacySyncErrors();

  const { data } = useQuery<SalefishSyncState>({
    queryKey: ["/api/projects", projectId, "salefish", "config"],
    // Through the one door, as a BACKGROUND read (K6): a five-minute poll
    // nobody asked for must never be what raises the step-up prompt or signs
    // someone out. React Query's signal stops it when the page goes.
    queryFn: ({ signal }) =>
      fetchJson<SalefishSyncState>(`/api/projects/${projectId}/salefish/config`, { signal, background: true }),
    enabled: !!projectId && isPlatformAdmin,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
    // A banner about a failed sync must never itself become an error screen.
    retry: false,
  });

  const banner = syncBanner(data, new Date(), t);
  if (!projectId || !banner || !isPlatformAdmin) return null;

  return (
    <div
      role="status"
      className={cn(
        "flex items-start gap-2.5 rounded-md border border-warning/25 bg-tint-warning px-3 py-2.5 text-on-tint-warning",
        className,
      )}
      data-testid="sync-status-banner"
      data-side={banner.side ?? undefined}
    >
      {banner.side === null ? (
        <RefreshCw className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
      ) : (
        <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
      )}
      <div className="min-w-0 flex-1 text-label">
        <p className="font-semibold">{banner.text}</p>
        {isPlatformAdmin && banner.adminDetail && (
          <p className="mt-0.5 break-words" data-testid="sync-status-admin-detail">
            {banner.adminDetail}
          </p>
        )}
      </div>
      {isPlatformAdmin && (
        <Link
          href={`/projects/${projectId}/integrations/salefish`}
          className="hit-target -my-0.5 inline-flex shrink-0 items-center gap-0.5 self-center rounded-xs text-label font-semibold text-current underline underline-offset-2 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
          data-testid="link-sync-settings"
        >
          {t("shellStatusLegacySyncLink", "Legacy Sync")}
          <ChevronRight className="size-4" aria-hidden="true" />
        </Link>
      )}
    </div>
  );
}
